import { ErrorData, ErrorObject } from '@/types';
import { message } from 'antd';
import i18next from 'i18next';

const NO_CARRIER_MATCHES_ERROR_CODE = 889000;
const NO_MATCHING_CARRIER_ERROR_CODE = 760001;
const SLOT_SELECTION_REQUIRED_CODE = 'SLOT_SELECTION_REQUIRED';

/**
 * R57-N2 — antd's default is 3 s and QA measured the refusal toast living
 * under ~1.5 s, on the one screen where the sentence is the whole point: a
 * merchant who missed it has no other record of why the order was refused.
 * Scoped to the 422 branch — a validation refusal is worth re-reading, a
 * generic network blip is not.
 */
const REFUSAL_TOAST_SECONDS = 8;

export const handleErrorMessage = (
  error?: ErrorData,
  defaultErrorMessage?: string,
  isMutation?: boolean,
) => {
  // Suppress UI error display for carrier-related error codes
  if (
    error?.code === NO_CARRIER_MATCHES_ERROR_CODE ||
    error?.code === NO_MATCHING_CARRIER_ERROR_CODE
  ) {
    return;
  }

  // Suppress UI error display for the pickup slot-selection gate — the
  // caller handles this inline (e.g. opening the slot picker) instead of
  // showing a toast.
  if (error?.code === SLOT_SELECTION_REQUIRED_CODE) {
    return;
  }

  const generalErrorMessage =
    error?.message ||
    defaultErrorMessage ||
    i18next.t(isMutation ? 'operation_failed' : 'unexpected_error');
  // Validation errors (422)
  if (error?.statusCode === 422 && error.object) {
    let hasArrayError = false;

    for (const key in error.object) {
      const value = error.object[key];
      if (!Array.isArray(value)) continue;

      // This loop was written for Laravel field-error arrays, which hold
      // STRINGS: {"contact.phone": ["The phone is invalid."]}. The bulk
      // refusal envelope puts an array of OBJECTS under the same roof
      // (`object.failed_orders`), and handing one of those to antd makes
      // React throw "Objects are not valid as a React child" — the merchant
      // gets a broken toast instead of the reason their order was refused.
      //
      // Only strings are renderable, so anything else falls through to
      // `error.message`, which is the localized refusal sentence and the
      // better thing to show anyway.
      // R57-F1 — joined, not passed as an array. antd renders a notice as
      // `<span>{content}</span>`, so an ARRAY content is reconciled member by
      // member: that is the `reconcileChildrenArray` frame the React crash
      // came from, and the only way to remove it for good is to never hand
      // antd an array. The type filter above already drops objects; this
      // makes the toast unable to crash even if a future envelope defeats it.
      const renderable = value.filter((entry): entry is string => typeof entry === 'string');
      if (renderable.length === 0) continue;

      message.error(renderable.join(' '), REFUSAL_TOAST_SECONDS);
      hasArrayError = true;
    }

    if (!hasArrayError) {
      message.error(generalErrorMessage, REFUSAL_TOAST_SECONDS);
    }

    return;
  }

  message.error(generalErrorMessage);
};

/**
 * Strip the bulk-row prefix from Laravel field-error keys.
 *
 * `/orders/bulk-preview` validates an ARRAY, so its errors come back keyed
 * `orders.0.contact.phone`. The create-order form posts a single order from a
 * single form, so "orders.0." names a row the merchant never saw and the
 * index is always 0 for them.
 *
 * Keys only — the messages themselves are the backend's own localized copy and
 * are left exactly as sent.
 */
export const stripBulkRowPrefix = (object?: ErrorObject): ErrorObject | undefined => {
  if (!object || typeof object !== 'object') return object;

  return Object.fromEntries(
    Object.entries(object).map(([key, value]) => [key.replace(/^orders\.\d+\./, ''), value]),
  ) as ErrorObject;
};
